import { cn, Skeleton, Tooltip } from '@heroui/react';
import { ArrowSeparate, Play } from 'iconoir-react';

import i18n from '~/common/helpers/i18n';

export type DeploymentReplicasCellProps = {
  replicaCount?: number;
  /** Minimum replicas when autoscaling is on; omit when it is off. */
  autoscalingMinReplicas?: number;
  /** Replicas running now, shown beside the autoscaling range; omit when unknown. */
  runningReplicas?: number;
};

export function DeploymentReplicasCell({
  replicaCount,
  autoscalingMinReplicas,
  runningReplicas
}: Readonly<DeploymentReplicasCellProps>) {
  if (replicaCount == null) {
    return <p className='w-[120px]'>—</p>;
  }

  if (autoscalingMinReplicas === undefined) {
    return <p className='w-[120px]'>{replicaCount}</p>;
  }

  return (
    <div className='flex w-[120px] flex-col gap-1 text-nowrap'>
      <Tooltip
        content={
          <div className='flex flex-col gap-0.5'>
            <p>{`${i18n.t('scenes.runtime.labels.minimum_replicas')}: ${autoscalingMinReplicas}`}</p>
            <p>{`${i18n.t('scenes.runtime.labels.maximum_replicas')}: ${replicaCount}`}</p>
            {runningReplicas === undefined ? null : (
              <p>{`${i18n.t('scenes.runtime.labels.running_replicas')}: ${runningReplicas}`}</p>
            )}
          </div>
        }
        placement='bottom'
      >
        <div className='flex w-fit items-center gap-1'>
          <span className='sr-only'>
            {i18n.t('scenes.runtime.labels.minimum_replicas')}
          </span>
          <p>{autoscalingMinReplicas}</p>
          <ArrowSeparate aria-hidden className='icon-smallest' />
          <span className='sr-only'>
            {i18n.t('scenes.runtime.labels.maximum_replicas')}
          </span>
          <p>{replicaCount}</p>
          {runningReplicas === undefined ? null : (
            <span
              className={cn(
                'ml-1 inline-flex items-center gap-0.5 rounded-full px-1.5 text-xs',
                runningReplicas > 0
                  ? 'bg-positive-container text-on-positive-container'
                  : 'bg-surface-container text-on-surface-variant'
              )}
            >
              <Play aria-hidden className='h-3 w-3' />
              <span className='sr-only'>
                {i18n.t('scenes.runtime.labels.running_replicas')}
              </span>
              <span>{runningReplicas}</span>
            </span>
          )}
        </div>
      </Tooltip>
      <p className='text-on-surface-variant'>
        {i18n.t('scenes.runtime.labels.autoscaling_on')}
      </p>
    </div>
  );
}

export function DeploymentReplicasCellSkeleton() {
  return (
    <div className='w-[120px]'>
      <Skeleton className='h-4 w-12 rounded' />
    </div>
  );
}
